// src/utils/DataMigrator.ts
import { Competition, Solution, User } from '../models';
import { SchemaChange, SchemaComparator } from './SchemaComparator';

/**
 * データマイグレーションを実行するユーティリティクラス
 */
export class DataMigrator {
  /**
   * コンペティションデータをマイグレーションする
   * @param competitions 既存のコンペティションデータ
   * @param csvData 新しいCSVデータ（ヘッダー行を含む）
   * @returns マイグレーション後のコンペティションデータ
   */
  static migrateCompetitions(competitions: Competition[], csvData: string): Competition[] {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSVデータが不正です。ヘッダー行と少なくとも1行のデータが必要です。');
    }
    
    // ヘッダー行を解析
    const newHeaders = this.parseCSVLine(lines[0]);
    
    // 既存のデータからヘッダーを取得
    const oldHeaders = Object.keys(competitions[0] || {});
    
    // スキーマ変更を検出
    const schemaChange = SchemaComparator.detectCompetitionSchemaChanges(oldHeaders, newHeaders);
    
    if (schemaChange.hasCriticalChanges) {
      throw new Error(`クリティカルなスキーマ変更が検出されました: ${schemaChange.criticalChanges}`);
    }
    
    if (!schemaChange.hasChanges) {
      // スキーマに変更がない場合は既存のデータを返す
      return competitions;
    }
    
    // 新しいCSVデータを解析
    const newCompetitions: Competition[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      const competition: any = {};
      
      // 各カラムの値を設定
      for (let j = 0; j < newHeaders.length; j++) {
        const header = newHeaders[j];
        const value = values[j];
        
        // 特殊なカラムの処理
        if (header === 'Tags') {
          competition[header] = value ? value.split('|') : [];
        } else if (header === 'ParticipantsCount') {
          competition[header] = parseInt(value, 10) || 0;
        } else {
          competition[header] = value;
        }
      }
      
      newCompetitions.push(competition as Competition);
    }
    
    // 既存のデータと新しいデータをマージ
    return this.mergeCompetitions(competitions, newCompetitions, schemaChange);
  }
  
  /**
   * ソリューションデータをマイグレーションする
   * @param solutions 既存のソリューションデータ
   * @param csvData 新しいCSVデータ（ヘッダー行を含む）
   * @returns マイグレーション後のソリューションデータ
   */
  static migrateSolutions(solutions: Solution[], csvData: string): Solution[] {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSVデータが不正です。ヘッダー行と少なくとも1行のデータが必要です。');
    }
    
    // ヘッダー行を解析
    const newHeaders = this.parseCSVLine(lines[0]);
    
    // 既存のデータからヘッダーを取得
    const oldHeaders = Object.keys(solutions[0] || {});
    
    // スキーマ変更を検出
    const schemaChange = SchemaComparator.detectSolutionSchemaChanges(oldHeaders, newHeaders);
    
    if (schemaChange.hasCriticalChanges) {
      throw new Error(`クリティカルなスキーマ変更が検出されました: ${schemaChange.criticalChanges}`);
    }
    
    if (!schemaChange.hasChanges) {
      // スキーマに変更がない場合は既存のデータを返す
      return solutions;
    }
    
    // 新しいCSVデータを解析
    const newSolutions: Solution[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      const solution: any = {};
      
      // 各カラムの値を設定
      for (let j = 0; j < newHeaders.length; j++) {
        const header = newHeaders[j];
        const value = values[j];
        
        // 特殊なカラムの処理
        if (header === 'UserIds') {
          solution[header] = value ? value.split('|') : [];
        } else if (header === 'Rank') {
          solution[header] = parseInt(value, 10) || 0;
        } else if (header === 'Score') {
          solution[header] = parseFloat(value) || 0;
        } else {
          solution[header] = value;
        }
      }
      
      newSolutions.push(solution as Solution);
    }
    
    // 既存のデータと新しいデータをマージ
    return this.mergeSolutions(solutions, newSolutions, schemaChange);
  }
  
  /**
   * ユーザーデータをマイグレーションする
   * @param users 既存のユーザーデータ
   * @param csvData 新しいCSVデータ（ヘッダー行を含む）
   * @returns マイグレーション後のユーザーデータ
   */
  static migrateUsers(users: User[], csvData: string): User[] {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSVデータが不正です。ヘッダー行と少なくとも1行のデータが必要です。');
    }
    
    // ヘッダー行を解析
    const newHeaders = this.parseCSVLine(lines[0]);
    
    // 既存のデータからヘッダーを取得
    const oldHeaders = Object.keys(users[0] || {});
    
    // スキーマ変更を検出
    const schemaChange = SchemaComparator.detectUserSchemaChanges(oldHeaders, newHeaders);
    
    if (schemaChange.hasCriticalChanges) {
      throw new Error(`クリティカルなスキーマ変更が検出されました: ${schemaChange.criticalChanges}`);
    }
    
    if (!schemaChange.hasChanges) {
      // スキーマに変更がない場合は既存のデータを返す
      return users;
    }
    
    // 新しいCSVデータを解析
    const newUsers: User[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      const user: any = {};
      
      // 各カラムの値を設定
      for (let j = 0; j < newHeaders.length; j++) {
        const header = newHeaders[j];
        const value = values[j];
        
        // 特殊なカラムの処理
        if (header === 'CurrentRank' || header === 'HighestRank') {
          user[header] = parseInt(value, 10) || 0;
        } else {
          user[header] = value;
        }
      }
      
      newUsers.push(user as User);
    }
    
    // 既存のデータと新しいデータをマージ
    return this.mergeUsers(users, newUsers, schemaChange);
  }
  
  /**
   * コンペティションデータをマージする
   * @param oldCompetitions 既存のコンペティションデータ
   * @param newCompetitions 新しいコンペティションデータ
   * @param schemaChange スキーマ変更の詳細
   * @returns マージ後のコンペティションデータ
   */
  private static mergeCompetitions(
    oldCompetitions: Competition[],
    newCompetitions: Competition[],
    schemaChange: SchemaChange
  ): Competition[] {
    // 既存のデータをSlugでインデックス化
    const competitionMap = new Map<string, Competition>();
    for (const comp of oldCompetitions) {
      competitionMap.set(comp.slug, comp);
    }
    
    // 新しいデータで更新または追加
    for (const newComp of newCompetitions) {
      const existingComp = competitionMap.get(newComp.slug);
      
      if (existingComp) {
        // 既存のデータを更新
        const updatedComp = { ...existingComp };
        
        // 新しいデータの各プロパティを適用
        for (const key in newComp) {
          if (Object.prototype.hasOwnProperty.call(newComp, key)) {
            (updatedComp as any)[key] = (newComp as any)[key];
          }
        }
        
        // リネームされたカラムの処理
        for (const { oldName, newName } of schemaChange.renamedColumns) {
          if ((existingComp as any)[oldName] !== undefined && (newComp as any)[newName] !== undefined) {
            (updatedComp as any)[newName] = (existingComp as any)[oldName];
            delete (updatedComp as any)[oldName];
          }
        }
        
        competitionMap.set(newComp.slug, updatedComp);
      } else {
        // 新しいデータを追加
        competitionMap.set(newComp.slug, newComp);
      }
    }
    
    // マップから配列に変換して返す
    return Array.from(competitionMap.values());
  }
  
  /**
   * ソリューションデータをマージする
   * @param oldSolutions 既存のソリューションデータ
   * @param newSolutions 新しいソリューションデータ
   * @param schemaChange スキーマ変更の詳細
   * @returns マージ後のソリューションデータ
   */
  private static mergeSolutions(
    oldSolutions: Solution[],
    newSolutions: Solution[],
    schemaChange: SchemaChange
  ): Solution[] {
    // 既存のデータをcompetitionSlug+rankでインデックス化
    const solutionMap = new Map<string, Solution>();
    for (const sol of oldSolutions) {
      const key = `${sol.competitionSlug}-${sol.rank}`;
      solutionMap.set(key, sol);
    }
    
    // 新しいデータで更新または追加
    for (const newSol of newSolutions) {
      const key = `${newSol.competitionSlug}-${newSol.rank}`;
      const existingSol = solutionMap.get(key);
      
      if (existingSol) {
        // 既存のデータを更新
        const updatedSol = { ...existingSol };
        
        // 新しいデータの各プロパティを適用
        for (const key in newSol) {
          if (Object.prototype.hasOwnProperty.call(newSol, key)) {
            (updatedSol as any)[key] = (newSol as any)[key];
          }
        }
        
        // リネームされたカラムの処理
        for (const { oldName, newName } of schemaChange.renamedColumns) {
          if ((existingSol as any)[oldName] !== undefined && (newSol as any)[newName] !== undefined) {
            (updatedSol as any)[newName] = (existingSol as any)[oldName];
            delete (updatedSol as any)[oldName];
          }
        }
        
        solutionMap.set(key, updatedSol);
      } else {
        // 新しいデータを追加
        solutionMap.set(key, newSol);
      }
    }
    
    // マップから配列に変換して返す
    return Array.from(solutionMap.values());
  }
  
  /**
   * ユーザーデータをマージする
   * @param oldUsers 既存のユーザーデータ
   * @param newUsers 新しいユーザーデータ
   * @param schemaChange スキーマ変更の詳細
   * @returns マージ後のユーザーデータ
   */
  private static mergeUsers(
    oldUsers: User[],
    newUsers: User[],
    schemaChange: SchemaChange
  ): User[] {
    // 既存のデータをuserIdでインデックス化
    const userMap = new Map<string, User>();
    for (const user of oldUsers) {
      userMap.set(user.userId, user);
    }
    
    // 新しいデータで更新または追加
    for (const newUser of newUsers) {
      const existingUser = userMap.get(newUser.userId);
      
      if (existingUser) {
        // 既存のデータを更新
        const updatedUser = { ...existingUser };
        
        // 新しいデータの各プロパティを適用
        for (const key in newUser) {
          if (Object.prototype.hasOwnProperty.call(newUser, key)) {
            (updatedUser as any)[key] = (newUser as any)[key];
          }
        }
        
        // リネームされたカラムの処理
        for (const { oldName, newName } of schemaChange.renamedColumns) {
          if ((existingUser as any)[oldName] !== undefined && (newUser as any)[newName] !== undefined) {
            (updatedUser as any)[newName] = (existingUser as any)[oldName];
            delete (updatedUser as any)[oldName];
          }
        }
        
        userMap.set(newUser.userId, updatedUser);
      } else {
        // 新しいデータを追加
        userMap.set(newUser.userId, newUser);
      }
    }
    
    // マップから配列に変換して返す
    return Array.from(userMap.values());
  }
  
  /**
   * CSV行を解析する
   * @param line CSV行
   * @returns 解析された値の配列
   */
  private static parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  }
}
